import {Fragment, useMemo} from 'react'
import {DayCell} from '../day-cell/DayCell'
import {useTranslation} from 'react-i18next'
import {it, Locale} from 'react-day-picker/locale'
import {MonthGrid} from '../month-grid/MonthGrid'
import {DayButton} from '../day-button/DayButton'
import {Weekday} from '../week-day/Weekday'
import {DateRange, DayPicker, MonthCaptionProps, MonthGridProps, MonthsProps, OnSelectHandler} from 'react-day-picker'
import {Flexbox} from '@/components/ui/flexbox/FlexBox'
import {RangeCalendarMonthCaption} from './RangeCalendarMonthCaption'

interface RangeCalendarPickerProps {
    onSelect: OnSelectHandler<DateRange>
    selected?: DateRange
}

export const RangeCalendarPicker = ({onSelect, selected}: RangeCalendarPickerProps) => {
    const {
        i18n: {language}
    } = useTranslation()
    // Map language codes to react-day-picker locales
    const locale = useMemo(() => {
        const localeMap: Record<string, Locale> = {
            it: it
        }

        return localeMap[language] || it
    }, [language])

    // Set default month to show the month containing the start date
    const defaultMonth = useMemo(() => {
        if (selected?.from) {
            return selected.from
        }
        return new Date() // Fallback to current month if no start date is selected
    }, [selected?.from])

    // Compose custom components
    const components = useMemo(
        () => ({
            MonthGrid: (props: MonthGridProps) => <MonthGrid {...props} isRange />,
            Day: DayCell,
            DayButton: DayButton,
            Weekday: Weekday,
            Nav: () => <Fragment />,
            Months: (props: MonthsProps) => (
                <Flexbox direction="row" gap={4} align="flex-start" style={{padding: '16px'}}>
                    {props.children}
                </Flexbox>
            ),
            MonthCaption: (props: MonthCaptionProps) => <RangeCalendarMonthCaption {...props} />
        }),
        []
    )

    return (
        <DayPicker
            required
            onSelect={onSelect}
            selected={selected}
            defaultMonth={defaultMonth}
            showOutsideDays
            fixedWeeks
            mode="range"
            numberOfMonths={2}
            components={components}
            locale={locale}
        />
    )
}
